import { Injectable, effect, inject } from '@angular/core';
import { MealPlanModel } from '@models/meal-plan/meal-plan.model';
import { OrderCartModel } from '@models/order/order-cart.model';
import { PendingOrderModel } from '@models/order/pending-order.model';
import { MealmomentService } from './meal-moment.service';

const STORAGE_KEY = 'pending_orders_cache';

@Injectable({ providedIn: 'root' })
export class PendingOrderService {
  private readonly mealmomentService = inject(MealmomentService);

  constructor() {
    effect(() => {
      this.mealmomentService.currentMealMoment();
      this.#writeStorage(this.#readValidEntries());
    });
  }

  save(customerId: number, order: OrderCartModel, mealPlan: MealPlanModel | null): void {
    const mealmoment = this.mealmomentService.currentMealMoment();
    if (!mealmoment) return;

    const entries = this.#readValidEntries().filter((entry) => entry.customerId !== customerId);
    entries.push({
      customerId,
      mealmomentId: mealmoment.id,
      day: this.#today(),
      order,
      mealPlan,
    });
    this.#writeStorage(entries);
  }

  consume(customerId: number): PendingOrderModel | null {
    const entries = this.#readValidEntries();
    const entry = entries.find((e) => e.customerId === customerId) ?? null;
    this.#writeStorage(entries.filter((e) => e.customerId !== customerId));
    return entry;
  }

  #today(): string {
    return new Date().toISOString().split('T')[0];
  }

  #readValidEntries(): PendingOrderModel[] {
    const mealmoment = this.mealmomentService.currentMealMoment();
    const today = this.#today();
    return this.#readStorage().filter(
      (entry) => entry.day === today && entry.mealmomentId === mealmoment?.id,
    );
  }

  #readStorage(): PendingOrderModel[] {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as PendingOrderModel[]) : [];
    } catch {
      return [];
    }
  }

  #writeStorage(entries: PendingOrderModel[]): void {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  }
}
